import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import { mainConsultationSystems } from "../section2-systems/systemMapping";
import { getCategoryColor } from "../../../platform/store/helper";

type Variant = "younger" | "older";

/**
 * "Making you younger" / "Making you older" side panel from Section 1
 * steps 3 & 4. Lists the systems that pull the bio-age in the given
 * direction, with their score % and a coloured tier chip.
 *
 * Source: real `score.systems[]` filtered by the `bioAgeContribution`
 * flag (1 → younger, -1 → older, 0 → not classified). The field is shipped
 * by the backend on each system row.
 *
 * Visual reference: Figma nodes 14711:146057 (younger only) and 14711:146211
 * (younger + older).
 */
const SystemsImpactCard: React.FC<{ variant: Variant }> = ({ variant }) => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();

  const wantedFlag = variant === "younger" ? 1 : -1;
  const items = mainConsultationSystems(data.systems ?? [])
    .map(({ subId, system }) => ({
      subId,
      name: system.name,
      score: typeof system.score === "number" ? system.score : 0,
      scoreCategory: system.scoreCategory,
      contribution: system.bioAgeContribution,
      approval: system.approval,
    }))
    .filter((s) => s.contribution === wantedFlag)
    .sort((a, b) =>
      variant === "younger" ? b.score - a.score : a.score - b.score
    );

  return (
    <Box
      sx={
        variant === "younger"
          ? styles.cardYounger(theme)
          : styles.cardOlder(theme)
      }
    >
      <Box sx={styles.header(theme)}>
        {variant === "younger" ? "Making you younger" : "Making you older"}
      </Box>
      <Box component="hr" sx={styles.divider(theme)} />
      <Box sx={styles.list}>
        {items.length === 0 ? (
          <Box sx={styles.emptyPlaceholder(theme)}>
            {variant === "younger"
              ? "No systems are making you younger yet."
              : "No systems are pulling your bio-age higher."}
          </Box>
        ) : (
          items.map((s) => (
            <Box key={s.subId} sx={styles.row(theme)}>
              <Box sx={styles.rowName(theme)}>{s.name}</Box>
              {s.approval === 4 && (
                <Box
                  sx={styles.rowScore(
                    theme,
                    getCategoryColor("%", s.score, theme, s.scoreCategory)
                  )}
                >
                  {s.score}%
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default SystemsImpactCard;

const styles = {
  cardYounger: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.S[10],
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "16px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    boxShadow:
      "0px 8px 20px rgba(0, 0, 0, 0.06), 0px 2px 8px rgba(0, 0, 0, 0.04)",
    width: "295px",
  }),
  cardOlder: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.W[10],
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "16px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    boxShadow:
      "0px 8px 20px rgba(0, 0, 0, 0.06), 0px 2px 8px rgba(0, 0, 0, 0.04)",
    width: "295px",
  }),
  header: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "32px",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  divider: (theme: Theme) => ({
    border: "none",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    margin: 0,
  }),
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  row: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    height: "40px",
    padding: "0 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  rowName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  rowScore: (theme: Theme, color: string) => ({
    backgroundColor: theme.colorTokens.N[30],
    borderRadius: "4px",
    padding: "4px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    letterSpacing: "-0.13px",
    color,
  }),
  emptyPlaceholder: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    padding: "16px 12px",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as const,
  }),
};
